import React from 'react';
import { useTranslation } from 'react-i18next';

import { Icon, Skeleton } from '../../../../components';
import type { PulseAheadItem } from '../../../../mock-data/types';
import { SectionCard } from '../SectionCard';
import { Lead, Value, Sub, Items, Item, Spark, Copy, Tag, ItemText, Chev, NotAvailable } from './LookingAhead.styles';

const LUMIO_SPARK = '/assets/acq/lumio-spark.svg';

export type LookingAheadProps = {
  value: string;
  span: string;
  sub: string;
  items: PulseAheadItem[];
  /** No source for the qualitative tailwind / watch-out cards: render a muted "not
   *  available yet" note in their place. */
  itemsMissing?: boolean;
  /** Reveal the in-progress tailwind / watch-out cards; when false they are omitted
   *  (the projected figure always shows). */
  showInProgress?: boolean;
  /** While true, the projected figure shows a placeholder (the endpoint is loading). */
  loading?: boolean;
};

/**
 * "Looking ahead" — the projected annual figure and a short list of tailwinds /
 * watch-outs, each row tinted by its `kind` (green / orange) and laid out
 * side-by-side on desktop. The caption is static chrome; the figure loads in.
 */
export const LookingAhead = ({ value, span, sub, items, itemsMissing = false, showInProgress = false, loading = false }: LookingAheadProps): React.ReactElement => {
  const { t } = useTranslation();

  // The in-progress cards are omitted unless they have real items or the flag is on.
  const showItems = !itemsMissing || showInProgress;

  // The forecast-item kind label is a fixed UI vocabulary (tailwind vs watch-out),
  // translated here keyed off its stable English source; the free-text `text` body
  // stays as-is (sample forecast content).
  const TAG_LABELS: Record<string, string> = {
    Tailwind: t('Tailwind'),
    Watch: t('Watch'),
  };
  // The caption + span are static chrome (closed vocabulary), localized keyed off their
  // stable English source; any other value passes through untranslated. (The span is an
  // interpolation VALUE, so localizing the wrapper key alone would leave it English.)
  const SUB_LABELS: Record<string, string> = {
    'Projected annual Net if current pace holds': t('Projected annual Net if current pace holds'),
  };
  const SPAN_LABELS: Record<string, string> = {
    '12 Months Trajectory': t('12 Months Trajectory'),
  };

  return (
    <SectionCard title={t('Looking ahead')} suffix={t('• {{span}}')} suffixPlaceholders={{ span: SPAN_LABELS[span] ?? span }}>
    <Lead>
      {/* Wrapped in `Value` so the placeholder inherits its font-size (em-based). */}
      <Value>{loading ? <Skeleton width={`${Math.max(4, value.length)}ch`} height="0.8em" radius={6} /> : value}</Value>
      <Sub text={SUB_LABELS[sub] ?? sub} tag="span" />
    </Lead>
    {showItems ? (
      itemsMissing ? (
        <NotAvailable text="Tailwinds and watch-outs aren't available yet" tag="span" />
      ) : (
        <Items>
          {items.map((it) => (
            <Item key={it.tag} $kind={it.kind} type="button">
              <Spark src={LUMIO_SPARK} alt="" width={16} height={16} />
              <Copy>
                <Tag text={TAG_LABELS[it.tag] ?? it.tag} tag="span" />
                <ItemText text={it.text} tag="span" />
              </Copy>
              <Chev>
                <Icon name="arrow-right-circle" size={24} />
              </Chev>
            </Item>
          ))}
        </Items>
      )
    ) : null}
    </SectionCard>
  );
};

export default LookingAhead;
